import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, FileArrowDown, EnvelopeSimple, FilePdf, FilePpt } from "@phosphor-icons/react";

const C = {
  panel: "#0A2826",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

export const MyExports = ({ compact = false }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = () => {
    setFailed(false);
    api.get("/exports")
      .then((r) => setRows(r.data?.exports || []))
      .catch(() => { setRows([]); setFailed(true); });
  };
  useEffect(load, []);

  const download = async (e) => {
    setBusy(e.export_id);
    try {
      const dl = await api.get(`/exports/${e.export_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(dl.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = e.filename || "deck";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Download failed — please try again");
    }
    setBusy(null);
  };

  return (
    <section data-testid="my-exports-section">
      {!compact && (
        <div className="flex items-center gap-2 mb-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] inline-flex items-center gap-2" style={{ color: C.tealSoft }}>
            <FileArrowDown size={16} /> My Exports
          </div>
        </div>
      )}
      {rows === null ? (
        <div className="text-sm text-white/40">Loading your exports…</div>
      ) : failed ? (
        <div className="rounded-xl p-6 border text-sm text-white/50 flex items-center justify-between gap-4"
          style={{ backgroundColor: C.panel, borderColor: "rgba(245, 158, 11, 0.25)" }}
          data-testid="my-exports-error">
          <span>Could not load your exports.</span>
          <button onClick={load} className="text-xs font-bold px-3 py-1.5 rounded-lg"
            style={{ backgroundColor: C.orange, color: "#000" }} data-testid="my-exports-retry">
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl p-6 border text-sm text-white/50"
          style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.12)" }}
          data-testid="my-exports-empty">
          No exports yet. Generate a pitch deck and every version is saved here forever — plus emailed straight to your inbox.
        </div>
      ) : (
        <div className={`rounded-xl border overflow-hidden ${compact ? "max-h-72 overflow-y-auto" : ""}`}
          style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.12)" }}>
          {rows.map((e, i) => (
            <div key={e.export_id} data-testid={`export-row-${e.export_id}`}
              className={`flex items-center gap-4 px-5 py-4 ${i > 0 ? "border-t border-white/5" : ""}`}>
              <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
                style={{ backgroundColor: e.kind === "pdf" ? "rgba(245,158,11,0.12)" : "rgba(10,139,133,0.18)" }}>
                {e.kind === "pdf"
                  ? <FilePdf size={18} weight="fill" style={{ color: C.orange }} />
                  : <FilePpt size={18} weight="fill" style={{ color: C.tealSoft }} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate text-white">{e.company_name || "Deck"}</div>
                <div className="text-[11px] text-white/40 truncate font-mono-num">{e.filename}</div>
              </div>
              <span className="hidden sm:inline-flex text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full shrink-0"
                style={{
                  backgroundColor: e.kind === "pdf" ? "rgba(245,158,11,0.12)" : "rgba(123,211,206,0.12)",
                  color: e.kind === "pdf" ? C.orange : C.tealSoft,
                }}>
                {e.kind === "pdf" ? "PDF" : "PPTX"}
              </span>
              {e.emailed && (
                <span className="hidden md:inline-flex items-center gap-1 text-[10px] text-white/35 shrink-0"
                  title="A copy was emailed to you">
                  <EnvelopeSimple size={12} /> Emailed
                </span>
              )}
              <div className="hidden sm:block text-[11px] text-white/40 shrink-0 w-32 text-right">{fmtDate(e.created_at)}</div>
              <div className="hidden md:block text-[11px] text-white/40 shrink-0 w-14 text-right font-mono-num">{fmtSize(e.byte_size)}</div>
              <button onClick={() => download(e)} disabled={busy === e.export_id}
                data-testid={`export-download-${e.export_id}`}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors shrink-0 disabled:opacity-50"
                style={{ backgroundColor: C.teal, color: "#fff" }}>
                <DownloadSimple size={13} weight="bold" />
                {busy === e.export_id ? "…" : "Download"}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default MyExports;
